import { assertDefined, isDefined, mapKeyBy } from '../../../lib/util';
import type { EventTimeline } from '../../core/Timelines';
import type {
  Currency,
  VoyageIncome_VoyageCharterFragment,
} from '../../../graphql';
import {
  PaymentUnit,
  useCreateVoyageCharterOrderFreightMutation,
  useUpdateVoyageCharterOrderFreightMutation,
} from '../../../graphql';
import { useCallback, useMemo, useState } from 'react';
import type { TimeData } from '../../core/TimeData';
import { isLoadEventTimeData } from '../../core/TimeData';
import {
  getConsumptionByPortCall,
  getPortCallIdsByOrder,
  getVoyageCharterBafIncomes,
} from './baf';
import type { BunkerConsumptionByPortCall } from '../costs/bunkerCost';
import { compact, sum } from 'lodash';
import type { ScenarioOrder } from '../../../lib/planningData';

import type { GlobalStore, User } from '../../../state/zustand';
import {
  useCurrencyRates,
  useCurrentUser,
  useDefaultCurrency,
} from '../../../state/zustand';
import type { BunkerPriceByGradeName } from '../../hooks/useBunkerPrices';
import { useBunkerPrices } from '../../hooks/useBunkerPrices';
import type { ClientId } from '../../core/ClientId';
import { getVoyageCharterCommissionIncomes } from './commission';
import type { LoadedAmount } from '../loadedAmounts';
import { getTimelineLoadedAmounts } from '../loadedAmounts';
import { type DefaultMap, mapGroupBy } from '@seaber/common/lib/collections';
import type { PnlData } from '../../hooks/usePnlData';
import type { Distances } from '../../distances/context';
import {
  getFreightByLoadedAmount,
  getFreightIncome,
  getFreightsByOrderId,
  type OrderFreight,
} from './orderFreight';
import { getVoyageCharterExtraFreightIncome } from './extraFreight';
import type { WorldscaleFreightRateMap } from '../../../lib/worldscaleFreight';

export type IncomeParam = {
  name: string;
  value: string | number | null;
  testId?: string;
};

export type Income = {
  type:
    | 'ORDER_FREIGHT'
    | 'VOYAGE_CHARTER_LUMPSUM'
    | 'EXTRA_FREIGHT'
    | 'BAF'
    | 'COMMISSION';
  name: string;
  value: number | null;
  orderId: number | null;
  voyageCharterId: number | null;
  params: IncomeParam[];
};

export type GetIncomeForLoadedAmount = (
  loadedAmounts: LoadedAmount[],
  voyageCharterIds: Set<number>,
  portCallIdsByOrder: Map<number, Set<ClientId>>,
  bunkerConsumptionByPortCall: BunkerConsumptionByPortCall,
) => Income[];

export function incomeForLoadedAmountGetter(
  orderById: Map<number, ScenarioOrder>,
  freightByOrderId: DefaultMap<number, OrderFreight[]>,
  voyageCharterById: Map<number, VoyageIncome_VoyageCharterFragment>,
  currencyRates: GlobalStore['currencyRates'],
  defaultCurrency: Currency,
  bunkerPriceByGradeName: BunkerPriceByGradeName,
  worldscaleFreightRates: WorldscaleFreightRateMap,
  freightOverrides?: FreightOverrideByOrder,
): GetIncomeForLoadedAmount {
  const orderVcRelsByIncomingVcId = mapGroupBy(
    Array.from(
      orderById.values(),
      (o): ScenarioOrder['incomingVoyageCharterRelation'] =>
        o.incomingVoyageCharterRelation,
    ).filter(isDefined),
    (o) => o.voyageCharterId,
  );
  return (
    loadedAmounts,
    incomingVoyageCharterIds,
    portCallIdsByOrder,
    bunkerConsumptionByPortCall,
  ) => {
    const incomes: Income[] = [];
    const freightIncomeByOrderId: Map<number, number> = new Map();

    /* Get freight for each loaded order */
    loadedAmounts.forEach((loadedAmount) => {
      const { orderId } = loadedAmount;
      const order = orderById.get(orderId);
      if (!order) return;

      const freight = getFreightByLoadedAmount(freightByOrderId, loadedAmount);
      const override = freightOverrides?.[orderId];
      if (!freight && !override) return;

      const overrideFreight = override
        ? {
            orderId: orderId,
            unit: override.freightUnit,
            price: override.freight,
            currency: freight?.currency ?? defaultCurrency,
            maxAmount: null,
            minAmount: null,
            lumpSumUnit: null,
            coaAssortmentMatchedBy: null,
          }
        : null;

      const { incomeAmount, params } = getFreightIncome(
        loadedAmount,
        overrideFreight || freight!,
        currencyRates,
        worldscaleFreightRates,
        order,
      );
      if (incomeAmount === null) return;

      if (overrideFreight && freight) {
        params.unshift({
          name: 'Original freight',
          value: `${freight.price} ${freight.currency}/${freight.unit}`,
        });
      }

      freightIncomeByOrderId.set(orderId, incomeAmount);

      if (
        incomeAmount === 0 &&
        order.incomingVoyageCharterRelation?.voyageCharterId &&
        voyageCharterById.get(
          order.incomingVoyageCharterRelation?.voyageCharterId,
        )?.lumpsum
      ) {
        // There is a lumpsum income so no need to show zero income from freights
        return;
      }

      incomes.push({
        type: 'ORDER_FREIGHT',
        name: `Order #${order.id} ${
          order.assortmentByAssortmentId?.name
        } ${order.locationByLoadPortId?.name} - ${
          order.locationByDischargePortId?.name
        }`,
        orderId,
        voyageCharterId:
          order.incomingVoyageCharterRelation?.voyageCharterId ?? null,
        value: incomeAmount,
        params,
      });
    });

    /* Get extra freights for each voyage charter */
    incomingVoyageCharterIds.forEach((vcId) => {
      const voyageCharter = voyageCharterById.get(vcId);
      if (!voyageCharter) return;

      if (voyageCharter.lumpsum) {
        const voyageCharterOrders =
          orderVcRelsByIncomingVcId
            .get(vcId)
            ?.map((node) =>
              node?.orderId ? orderById.get(node.orderId) : null,
            )
            .filter(isDefined) ?? [];

        incomes.push({
          type: 'VOYAGE_CHARTER_LUMPSUM',
          name: 'Total contract freight',
          orderId: null,
          voyageCharterId: voyageCharter.id,
          value: Number(voyageCharter.lumpsum),
          params: voyageCharterOrders
            .map(
              (order) =>
                `#${order?.id ?? 'N/A'} ${
                  order?.assortmentByAssortmentId?.name
                } ${order?.locationByLoadPortId?.name} - ${
                  order?.locationByDischargePortId?.name
                }`,
            )
            .map((orderString) => ({
              name: 'Order',
              value: orderString,
            })),
        });
      }

      const extraFreightIncomes = getVoyageCharterExtraFreightIncome(
        voyageCharter,
        loadedAmounts,
        freightIncomeByOrderId,
        orderVcRelsByIncomingVcId,
      );

      const bafIncomes = getVoyageCharterBafIncomes(
        voyageCharter,
        loadedAmounts,
        orderById,
        freightIncomeByOrderId,
        portCallIdsByOrder,
        bunkerConsumptionByPortCall,
        defaultCurrency,
        bunkerPriceByGradeName,
      );

      incomes.push(...extraFreightIncomes, ...bafIncomes);

      /* Get all incomes related to this voyage charter for commission calculation */
      const voyageCharterOrderIds = new Set<number>();
      for (const vcRel of orderVcRelsByIncomingVcId.get(vcId) ?? []) {
        voyageCharterOrderIds.add(vcRel.orderId);
      }

      const voyageCharterIncomes: Income[] = [
        ...bafIncomes,
        ...extraFreightIncomes,
        ...incomes.filter(
          (income) =>
            (income.type === 'ORDER_FREIGHT' ||
              income.type === 'VOYAGE_CHARTER_LUMPSUM') &&
            income.orderId &&
            voyageCharterOrderIds.has(income.orderId),
        ),
      ];

      const commissionIncomes = getVoyageCharterCommissionIncomes(
        voyageCharter,
        voyageCharterIncomes,
        loadedAmounts,
      );

      incomes.push(...commissionIncomes);
    });

    return incomes;
  };
}

export const useVoyageIncome = (
  incomingVoyageChartersById: PnlData['incomingVoyageChartersById'],
  worldscaleFreightRates: PnlData['worldscaleFreightRates'],
) => {
  const currentUser = useCurrentUser();
  const currencyRates = useCurrencyRates();
  const defaultCurrency = useDefaultCurrency();
  const { bunkerPriceByGradeName } = useBunkerPrices();

  const getTcTimelineIncome = useCallback(
    async (
      timeline: EventTimeline,
      focusedOrderIds?: Set<number>,
      freightOverrides?: FreightOverrideByOrder,
    ): Promise<Income[]> => {
      assertDefined(currentUser, 'logged in user');

      const tds = timeline.voyageGroups.at(0)?.events ?? [];
      const orders = timeline.voyageGroups.at(0)?.orders ?? [];
      const orderById = mapKeyBy(orders, (o) => o.id);
      const timelineVoyageCharterIds = new Set(
        compact(
          orders.map((o) => o.incomingVoyageCharterRelation?.voyageCharterId),
        ),
      );

      const freightByOrderId = getFreightsByOrderId(
        incomingVoyageChartersById,
        orderById,
        {
          addMissingFreights: !!freightOverrides,
        },
      );

      const loadedAmounts = getTimelineLoadedAmounts(timeline, focusedOrderIds);

      const getIncome = incomeForLoadedAmountGetter(
        orderById,
        freightByOrderId,
        incomingVoyageChartersById,
        currencyRates,
        defaultCurrency,
        bunkerPriceByGradeName,
        worldscaleFreightRates,
        freightOverrides,
      );

      const portCallIdsByOrder = getPortCallIdsByOrder(tds);
      const bunkerConsumptionByPortCall = getConsumptionByPortCall(tds);

      return getIncome(
        loadedAmounts,
        timelineVoyageCharterIds,
        portCallIdsByOrder,
        bunkerConsumptionByPortCall,
      );
    },
    [
      bunkerPriceByGradeName,
      currencyRates,
      currentUser,
      defaultCurrency,
      incomingVoyageChartersById,
      worldscaleFreightRates,
    ],
  );

  return { getTcTimelineIncome };
};

export type FreightOverride = { freight: number; freightUnit: PaymentUnit };
export type FreightOverrideByOrder = Record<number, FreightOverride>;

export function useFreightOverrides(orderById: Map<number, ScenarioOrder>) {
  const [createOrderFreight] = useCreateVoyageCharterOrderFreightMutation();
  const [updateOrderFreight] = useUpdateVoyageCharterOrderFreightMutation();

  const [freightOverrides, setFreightOverrides] =
    useState<FreightOverrideByOrder>({});

  const toggleFreightOverride = useCallback(
    (orderId: number, existingValue: number | null) => {
      setFreightOverrides((overrides) => {
        const isEnabled = !!overrides[orderId];
        if (isEnabled) {
          const { [orderId]: _omitted, ...nextOverrides } = overrides;
          return nextOverrides;
        } else {
          return {
            ...overrides,
            [orderId]: {
              freight: existingValue ?? 0,
              freightUnit: PaymentUnit.MT,
            },
          };
        }
      });
    },
    [],
  );

  const setFreightOverride = useCallback(
    (orderId: number, freightOverride: FreightOverride) => {
      setFreightOverrides((overrides) => {
        return {
          ...overrides,
          [orderId]: freightOverride,
        };
      });
    },
    [],
  );

  const persistOverride = useCallback(
    async (orderId: number) => {
      const override = freightOverrides[orderId];
      if (!override) {
        console.error('No override found for order', orderId);
        return;
      }

      const order = orderById.get(orderId);
      const vcRel = order?.incomingVoyageCharterRelation;

      if (!vcRel) {
        console.error('No voyage charter relation found for order', orderId);
        return;
      }

      const persistedFreight =
        vcRel.voyageCharterOrderFreightsByOrderVoyageCharterRelationId.nodes.at(
          0,
        );

      if (persistedFreight) {
        await updateOrderFreight({
          variables: {
            input: {
              id: persistedFreight.id,
              voyageCharterOrderFreightPatch: {
                freightAmount: override.freight,
                freightUnit: override.freightUnit,
              },
            },
          },
        });
      } else {
        await createOrderFreight({
          variables: {
            input: {
              voyageCharterOrderFreight: {
                freightAmount: override.freight,
                freightUnit: override.freightUnit,
                orderVoyageCharterRelationId: vcRel.id,
              },
            },
          },
        });
      }

      setFreightOverrides((currentOverrides) => {
        const { [orderId]: _omitted, ...nextOverrides } = currentOverrides;
        return nextOverrides;
      });
    },
    [freightOverrides, orderById, updateOrderFreight, createOrderFreight],
  );

  return useMemo(
    () => ({
      freightOverrides,
      persistOverride,
      setFreightOverride,
      toggleFreightOverride,
    }),
    [
      freightOverrides,
      persistOverride,
      setFreightOverride,
      toggleFreightOverride,
    ],
  );
}

export const isEuEtsCostEnabledForUser = (user: User | null) =>
  !!user?.rootOrganization?.euEtsCosts;

export function getTDIncomeAndMassDistance(
  timeData: TimeData[],
  getIncomeForLoadedAmount: GetIncomeForLoadedAmount | undefined,
  distances: Distances,
  opts?: { firstIndex: number; lastIndex: number },
) {
  let totalIncome = 0;
  let totalMassDistance = 0;
  let totalCost = 0;
  let totalTcCost = 0;
  let totalPortCostEstimate = 0;
  const loadedVoyageCharterIds = new Set<number>();
  const loadedAmounts: LoadedAmount[] = [];
  const missingIncomeOrderIds: number[] = [];

  for (
    let i = opts?.firstIndex ?? 0;
    i <= (opts?.lastIndex ?? timeData.length - 1);
    ++i
  ) {
    const td = timeData[i];
    if (isLoadEventTimeData(td)) {
      const {
        order: {
          id,
          dischargePortId,
          loadPortId,
          incomingVoyageCharterRelation,
        },
      } = td.event;
      if (!loadPortId) {
        continue;
      }

      loadedAmounts.push({
        orderId: id,
        loadedMass: td.mass,
        loadedVolume: td.volume,
        loadedPcs: td.pcs,
        cargoAmount: Number(td.event.order.cargoAmount),
        cargoUnit: td.event.order.cargoUnitByCargoUnitId?.name || null,
      });

      const voyageCharterId =
        incomingVoyageCharterRelation?.voyageCharterId ?? null;

      if (voyageCharterId !== null) {
        loadedVoyageCharterIds.add(voyageCharterId);
      }

      const distance = distances.get(loadPortId, dischargePortId, false)?.dist;
      if (!distance) {
        continue;
      }
      const massDistance = td.mass * distance;
      totalMassDistance += massDistance;
    }
    totalCost += td.costs.totalCost;
    totalTcCost += td.costs.tcCost;
    totalPortCostEstimate += td.costs.portCostEstimate ?? 0;
  }

  const portCallIdsByOrder = getPortCallIdsByOrder(timeData);
  const bunkerConsumptionByPortCall = getConsumptionByPortCall(timeData);

  if (getIncomeForLoadedAmount) {
    totalIncome = sum(
      getIncomeForLoadedAmount(
        loadedAmounts,
        loadedVoyageCharterIds,
        portCallIdsByOrder,
        bunkerConsumptionByPortCall,
      ).map(({ value, orderId }) => {
        if (!value && orderId !== null) {
          missingIncomeOrderIds.push(orderId);
        }
        return value ?? 0;
      }),
    );
  }

  return {
    income: totalIncome,
    massDistance: totalMassDistance,
    missingIncomeOrderIds,
    totalCost,
    tcCost: totalTcCost,
    portCostEstimate: totalPortCostEstimate,
    loadedAmounts,
    loadedVoyageCharterIds,
  };
}
